import { useEffect } from 'react';
import { useChat } from '../context/Context';
import {emitEventGA} from "../utils/emitEventGA";

const isManualOperatorTextMessage = (message) => {
    const type = String(message?.type || '').toLowerCase();

    return (
        type === 'client' &&
        message?.system === false &&
        typeof message?.text === 'string' &&
        Boolean(message.text.trim())
    );
};

export const useDialogSocketHandlers = ({
                                            socketInstance,
                                            dialogId,
                                            messagesRequest,
                                            setDialogActive,
                                            fetchDialogId,
                                            setIsAddYourDataVisible,
                                            setData,
                                            isWidgetShown,
                                            setIsOnline,
                                            onOperatorMessage,
                                        }) => {
    const {widgetData: {ga4_code}, engineAutomations, setDialogId} = useChat()

    useEffect(() => {
        if (!socketInstance) return;

        const ensureDialogReady = async () => {
            if (!dialogId?.data?.id) {
                try {
                    await fetchDialogId({ method: 'POST' });
                } catch (err) {
                    console.error('❌ Error fetching dialogId in socket handler:', err);
                }
            }
        };

        const dialogContactsHandler = () => {
            emitEventGA('contact_form_sent', {}, ga4_code);

            setIsAddYourDataVisible(true);
        };
        const dialogPassHandler = async () => {
            await ensureDialogReady();
            messagesRequest();
        };
        const dialogConnectHandler = async (data) => {
            if (!data?.auto) {
                emitEventGA('chat_established', {}, ga4_code);
            }

            const connectedId = Number(data?.id);
            const currentId = Number(dialogId?.data?.id);

            if (connectedId && currentId && connectedId !== currentId) {
                setDialogId({ data: { id: connectedId } });
                localStorage.setItem('dialogStatus', 'active');
                setDialogActive(true);
                return;
            }

            await ensureDialogReady();
            messagesRequest();
        };
        const dialogStatusHandler = async ({ id, status }) => {
            const currentId = Number(dialogId?.data?.id);
            if (id && currentId && Number(id) !== currentId) return;

            await ensureDialogReady();
            localStorage.setItem('dialogStatus', status === 'unresolved' ? 'active' : 'closed');
            setDialogActive(status === 'unresolved');

            if (dialogId?.data?.id) messagesRequest();
            if (status === 'resolved') {
                setData(null);
            }
        };

        const dialogOperatorIsOnline = ({ status }) => {
            setIsOnline(status === 'online');
        };

        const dialogHandler = async (data) => {
            await ensureDialogReady();

            let operatorMessage = isManualOperatorTextMessage(data) ? data : null;
            let messagesResponse = null;

            if (!operatorMessage && String(data?.type || '').toLowerCase() === 'client') {
                messagesResponse = await messagesRequest();

                const incomingMessages = messagesResponse?.data?.chat?.messages?.income || [];
                const receivedMessage = incomingMessages.find((message) => message.id === data?.id);

                if (receivedMessage) {
                    const normalizedMessage = { ...receivedMessage, type: 'client' };
                    if (isManualOperatorTextMessage(normalizedMessage)) {
                        operatorMessage = normalizedMessage;
                    }
                }
            }

            if (isWidgetShown && operatorMessage) {
                onOperatorMessage?.(operatorMessage);
	            }

	            try {
	                const raw = data?.created_at || data?.time || data?.updated_at;
	                const parsed = raw ? new Date(raw).getTime() : NaN;
	                engineAutomations?.notifyLastMessage(Number.isFinite(parsed) ? parsed : undefined);
	            } catch {
	                engineAutomations?.notifyLastMessage();
	            }
	
            if (operatorMessage) {
                const messageAudio = window.messageAudio;
                let parentSound = null;

                if (messageAudio) {
                    if (isWidgetShown) {
                        parentSound = messageAudio.src;
                    } else {
                        try {
                            messageAudio.currentTime = 0;
                            await messageAudio.play();
                        } catch (err) {
                            parentSound = messageAudio.src;
                            console.warn('🔇 Unable to play audio in widget:', err);
                        }
                    }
                }

                window.parent.postMessage(
                    {
                        type: 'dialog.message',
                        data: operatorMessage,
                        sound: parentSound,
                    },
                    '*'
                );
            }

            if (!messagesResponse) messagesRequest();
            localStorage.setItem('dialogStatus', 'active');
            setDialogActive(true);
        };

        socketInstance.on('dialog.contacts', dialogContactsHandler);
        socketInstance.on('dialog.pass', dialogPassHandler);
        socketInstance.on('dialog.connect', dialogConnectHandler);
        socketInstance.on('dialog.status', dialogStatusHandler);
        socketInstance.on('dialog.operator', dialogOperatorIsOnline);
        socketInstance.on('dialog.message.send', dialogHandler);
        socketInstance.on('dialog.message.edit', messagesRequest);
        socketInstance.on('dialog.message.delete', ({ id }) => {
            setData((prev) => prev?.filter((m) => m.id !== id));
        });

        return () => {
            socketInstance.off('dialog.contacts', dialogContactsHandler);
            socketInstance.off('dialog.pass', dialogPassHandler);
            socketInstance.off('dialog.connect', dialogConnectHandler);
            socketInstance.off('dialog.status', dialogStatusHandler);
            socketInstance.off('dialog.operator', dialogOperatorIsOnline);
            socketInstance.off('dialog.message.send', dialogHandler);
            socketInstance.off('dialog.message.edit', messagesRequest);
            socketInstance.off('dialog.message.delete', messagesRequest);
        };
    }, [
        socketInstance,
        dialogId?.data?.id,
        messagesRequest,
        setDialogActive,
        setDialogId,
        setIsAddYourDataVisible,
        setData,
        isWidgetShown,
        setIsOnline,
        fetchDialogId,
        onOperatorMessage,
        engineAutomations,
        ga4_code,
    ]);
};
